import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Calendar, Clock, BookOpen, Loader2 } from 'lucide-react';
import { Quiz } from '@/types/quiz';
import { QuizCard } from './QuizCard';

interface PlayerProgram {
  id: string;
  status: string;
  assigned_at: string;
  program?: {
    title: string;
    description?: string;
    is_active: boolean;
  };
  player?: {
    profile?: {
      first_name?: string;
      last_name?: string;
    };
  };
}

interface ProgramInfoCardProps {
  playerProgram: PlayerProgram;
  quizzes: Quiz[];
  quizzesLoading: boolean;
  onTakeQuiz: (quiz: Quiz) => void;
}

export const ProgramInfoCard: React.FC<ProgramInfoCardProps> = ({
  playerProgram,
  quizzes,
  quizzesLoading,
  onTakeQuiz,
}) => {
  const playerName = `${playerProgram.player?.profile?.first_name || ''} ${playerProgram.player?.profile?.last_name || ''}`.trim();

  return (
    <Card className="mb-8">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-2xl mb-2">{playerProgram.program?.title}</CardTitle>
            <p className="text-muted-foreground mb-4">
              {playerProgram.program?.description || 'No description available'}
            </p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {playerName}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Assigned: {new Date(playerProgram.assigned_at).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {playerProgram.program?.is_active ? 'Active Program' : 'Inactive Program'}
              </div>
            </div>
          </div>
          <Badge variant={playerProgram.status === 'active' ? 'default' : 'secondary'}>
            {playerProgram.status}
          </Badge>
        </div>
      </CardHeader>
      
      {/* Quizzes Section */}
      {(quizzes.length > 0 || quizzesLoading) && (
        <CardContent className="pt-0">
          <div className="border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold">Available Quizzes</h3>
            </div>
            
            {quizzesLoading ? (
              <div className="flex items-center gap-3 justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="text-muted-foreground">Loading quizzes...</span>
              </div>
            ) : quizzes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No quizzes available for this program</p>
              </div>
            ) : (
              <div className="space-y-3">
                {quizzes.map((quiz, index) => (
                  <QuizCard
                    key={quiz.id}
                    quiz={quiz}
                    index={index}
                    onTakeQuiz={onTakeQuiz}
                  />
                ))}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};